import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cx } from "@/lib/cx";
import { LAYER } from "./surface";

/**
 * A label for a control that has no room for one.
 *
 * Two rules, because a tooltip is the easiest accessibility mistake in a UI
 * kit:
 *
 * 1. **A tooltip is never the only way to read something.** It does not exist
 *    on touch and it does not exist for a screen reader unless the trigger also
 *    carries an accessible name. Icon buttons still need `aria-label`; this
 *    only tells a mouse user what the icon means.
 * 2. **It never holds an action.** Anything clickable goes in a
 *    {@link Popover} or a {@link Menu}, both of which a keyboard can reach.
 *
 * Radix requires a provider; {@link OverlayProvider} mounts it, so a feature
 * only ever uses this component.
 */

export type TooltipProps = {
  content: ReactNode;
  /** Must be a single element that can take a ref — Radix clones it. */
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  /** Renders the trigger untouched. For a disabled or dynamic case. */
  disabled?: boolean;
  delayDuration?: number;
};

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  sideOffset = 6,
  disabled = false,
  delayDuration,
}: TooltipProps) {
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          data-busee-anim="pop"
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cx(
            // Inverted and opaque rather than a material: a tooltip is the one
            // popper that may land on top of another popper, and two stacked
            // blurs render as a smear. The inverted fill separates from
            // everything in the app, in both themes, at 15:1.
            //
            // `rounded-control` (10px) — the nested-control radius rung,
            // because this is the smallest surface in the app and radii grow
            // with the surface (IOS-DESIGN §7).
            "busee-tooltip pointer-events-none max-w-[16rem] rounded-control border-2 border-ps1-orange bg-ps1-bg px-2 py-1",
            "text-caption text-ink shadow-pop",
            "origin-(--radix-popper-transform-origin)",
            LAYER.overlay,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/**
 * The provider. Mounted once by {@link OverlayProvider} — a feature should not
 * need this directly.
 *
 * `skipDelayDuration` is what makes a row of icon buttons feel right: the first
 * tooltip waits, and moving along the row shows the rest immediately.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={400} skipDelayDuration={200}>
      {children}
    </TooltipPrimitive.Provider>
  );
}
